import { z } from "zod";

import type {
  ActivityAdapter,
  ActivityCompletionMetrics
} from "@/app/integration/contracts";
import { getSoundBubbleFeedbackCue } from "@/audio/curriculum-catalog/chinese-feedback-cues";
import type { SoundBubblePayload } from "@/content/chinese";
import {
  boundedTextSchema,
  kebabCaseIdentifierSchema,
  localResourceReferenceSchema
} from "@/domain/schemas";
import {
  advanceHintEscalation,
  type ActiveHintStage
} from "@/game/feedback";
import {
  getAdjacentId,
  type InputMethod,
  type NavigationDirection
} from "@/game/input";
import {
  choiceMatchItemSchema,
  createChoiceActivityState,
  getChoiceKeyboardOrder,
  reduceChoiceActivity,
  singleChoicePayloadSchema,
  type ChoiceActivityState,
  type SingleChoicePayload
} from "@/game/templates/choice-match";

const soundBubblePayloadSchema = z.strictObject({
  spokenText: boundedTextSchema(240),
  soundAudio: localResourceReferenceSchema,
  question: boundedTextSchema(240),
  choices: z.array(choiceMatchItemSchema).min(2).max(12),
  correctChoiceId: kebabCaseIdentifierSchema,
  hint: boundedTextSchema(240)
});

function toChoicePayload(payload: SoundBubblePayload): SingleChoicePayload {
  return {
    kind: "single-choice",
    choices: payload.choices,
    correctChoiceId: payload.correctChoiceId
  };
}

export function parseSoundBubblePayload(value: unknown): SoundBubblePayload {
  const payload = soundBubblePayloadSchema.parse(value);
  singleChoicePayloadSchema.parse(toChoicePayload(payload));
  return payload;
}

export interface SoundBubbleActivityState {
  readonly activity: ChoiceActivityState;
  readonly soundAudio: string;
  readonly spokenText: string;
}

export type SoundBubbleAction =
  | {
      readonly type: "choose";
      readonly choiceId: string;
      readonly inputMethod: InputMethod;
    }
  | {
      readonly type: "focus-choice";
      readonly choiceId: string;
    }
  | {
      readonly type: "move-focus";
      readonly direction: NavigationDirection;
    }
  | { readonly type: "activate-focused" }
  | { readonly type: "request-hint" }
  | { readonly type: "replay" };

export interface SoundBubbleChoiceRenderModel {
  readonly focused: boolean;
  readonly hinted: boolean;
  readonly id: string;
  readonly label: string;
  readonly selected: boolean;
  readonly visible: boolean;
}

export interface SoundBubbleFeedbackRenderModel {
  readonly audioPath: string;
  readonly kind: "hint" | "retry";
  readonly message: string;
  readonly playbackRequest: number;
  readonly stage: ActiveHintStage;
}

export interface SoundBubbleRenderModel {
  readonly choices: readonly SoundBubbleChoiceRenderModel[];
  readonly feedback: SoundBubbleFeedbackRenderModel | null;
  readonly focusedChoiceId: string | null;
  readonly keyboardOrder: readonly string[];
  readonly question: string;
  readonly soundAudio: string;
  readonly spokenText: string;
}

function createSoundBubbleState(
  payload: SoundBubblePayload
): SoundBubbleActivityState {
  return {
    activity: createChoiceActivityState(toChoicePayload(payload)),
    soundAudio: payload.soundAudio,
    spokenText: payload.spokenText
  };
}

function getVisibleChoiceIds(
  payload: SoundBubblePayload,
  stage: ChoiceActivityState["hintState"]["stage"]
): readonly string[] {
  if (stage === "none" || stage === "gentle-hint") {
    return payload.choices.map(({ id }) => id);
  }

  const firstAlternative = payload.choices.find(
    ({ id }) => id !== payload.correctChoiceId
  );

  return payload.choices
    .filter(
      ({ id }) =>
        id === payload.correctChoiceId || id === firstAlternative?.id
    )
    .map(({ id }) => id);
}

function choose(
  state: ChoiceActivityState,
  payload: SoundBubblePayload,
  choiceId: string,
  inputMethod: InputMethod
): ChoiceActivityState {
  const templatePayload = toChoicePayload(payload);
  const selected = reduceChoiceActivity(templatePayload, state, {
    type: "select-choice",
    choiceId,
    inputMethod
  });

  return reduceChoiceActivity(templatePayload, selected, { type: "submit" });
}

function reduceSoundBubble(
  state: SoundBubbleActivityState,
  payload: SoundBubblePayload,
  action: SoundBubbleAction
): SoundBubbleActivityState {
  switch (action.type) {
    case "choose":
      if (
        !getVisibleChoiceIds(
          payload,
          state.activity.hintState.stage
        ).includes(action.choiceId)
      ) {
        return state;
      }

      return {
        ...state,
        activity: choose(
          state.activity,
          payload,
          action.choiceId,
          action.inputMethod
        )
      };
    case "focus-choice":
      if (
        !getVisibleChoiceIds(payload, state.activity.hintState.stage).includes(
          action.choiceId
        )
      ) {
        return state;
      }

      return {
        ...state,
        activity: {
          ...state.activity,
          focusedChoiceId: action.choiceId,
          lastInputMethod: "keyboard"
        }
      };
    case "move-focus": {
      if (state.activity.status === "complete") {
        return state;
      }

      const focusedChoiceId = getAdjacentId(
        getVisibleChoiceIds(
          payload,
          state.activity.hintState.stage
        ),
        state.activity.focusedChoiceId,
        action.direction
      );

      if (focusedChoiceId === null) {
        return state;
      }

      return {
        ...state,
        activity: {
          ...state.activity,
          focusedChoiceId,
          lastInputMethod: "keyboard"
        }
      };
    }
    case "activate-focused": {
      const focusedChoiceId = state.activity.focusedChoiceId;
      const visibleChoiceIds = getVisibleChoiceIds(
        payload,
        state.activity.hintState.stage
      );

      if (
        focusedChoiceId === null ||
        !visibleChoiceIds.includes(focusedChoiceId)
      ) {
        return state;
      }

      return {
        ...state,
        activity: choose(
          state.activity,
          payload,
          focusedChoiceId,
          "keyboard"
        )
      };
    }
    case "request-hint": {
      const hintState = advanceHintEscalation(state.activity.hintState);
      const visibleChoiceIds = getVisibleChoiceIds(payload, hintState.stage);
      const currentFocus = state.activity.focusedChoiceId;

      return {
        ...state,
        activity: {
          ...state.activity,
          feedback: null,
          focusedChoiceId:
            currentFocus === null
              ? null
              : visibleChoiceIds.includes(currentFocus)
                ? currentFocus
                : (visibleChoiceIds[0] ?? null),
          hintState
        }
      };
    }
    case "replay":
      return createSoundBubbleState(payload);
  }
}

function getSoundBubbleMetrics(
  state: SoundBubbleActivityState
): ActivityCompletionMetrics {
  const completionScore = state.activity.completionScore;
  const completed =
    state.activity.status === "complete" && completionScore !== null;

  return {
    completed,
    score: completed ? completionScore.score : 0,
    correctRate: completed ? completionScore.correctRate : 0,
    hintCount: state.activity.hintState.hintCount,
    retryCount: state.activity.retryCount
  };
}

function getFeedback(
  state: SoundBubbleActivityState,
  payload: SoundBubblePayload
): SoundBubbleFeedbackRenderModel | null {
  if (state.activity.feedback?.kind === "retry") {
    const cue = getSoundBubbleFeedbackCue(
      payload,
      state.activity.feedback.hintStage,
      "retry"
    );

    return {
      audioPath: cue.audioPath,
      kind: "retry",
      message: cue.text,
      playbackRequest:
        state.activity.hintState.hintCount + state.activity.retryCount,
      stage: state.activity.feedback.hintStage
    };
  }

  const stage = state.activity.hintState.stage;
  if (stage === "none") {
    return null;
  }

  const cue = getSoundBubbleFeedbackCue(payload, stage, "hint");

  return {
    audioPath: cue.audioPath,
    kind: "hint",
    message: cue.text,
    playbackRequest:
      state.activity.hintState.hintCount + state.activity.retryCount,
    stage
  };
}

function toSoundBubbleRenderModel(
  state: SoundBubbleActivityState,
  payload: SoundBubblePayload
): SoundBubbleRenderModel {
  const visibleChoiceIds = getVisibleChoiceIds(
    payload,
    state.activity.hintState.stage
  );
  const keyboardOrder = getChoiceKeyboardOrder(
    toChoicePayload(payload)
  ).filter((choiceId) => visibleChoiceIds.includes(choiceId));

  return {
    choices: payload.choices.map((choice) => ({
      focused: state.activity.focusedChoiceId === choice.id,
      hinted:
        state.activity.hintState.stage === "replay-demonstration" &&
        choice.id === payload.correctChoiceId,
      id: choice.id,
      label: choice.label,
      selected: state.activity.selectedChoiceIds.includes(choice.id),
      visible: visibleChoiceIds.includes(choice.id)
    })),
    feedback: getFeedback(state, payload),
    focusedChoiceId: state.activity.focusedChoiceId,
    keyboardOrder,
    question: payload.question,
    soundAudio: state.soundAudio,
    spokenText: state.spokenText
  };
}

export const soundBubbleAdapter: ActivityAdapter<
  SoundBubblePayload,
  SoundBubbleActivityState,
  SoundBubbleAction,
  SoundBubbleRenderModel
> = {
  activityType: "sound-bubbles",
  parsePayload: parseSoundBubblePayload,
  createInitialState: (payload, context) => {
    void context;
    return createSoundBubbleState(payload);
  },
  reduce: (state, payload, action, context) => {
    void context;
    return reduceSoundBubble(state, payload, action);
  },
  getMetrics: (state, payload) => {
    void payload;
    return getSoundBubbleMetrics(state);
  },
  toRenderModel: toSoundBubbleRenderModel
};

export const soundBubbleHostActions = {
  requestHint: () => ({ type: "request-hint" }) as const,
  replay: () => ({ type: "replay" }) as const
};
